import { IStateInit } from "@gtmhub/core/routing";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { IPromise, IScope } from "angular";
import { createTeamsTree } from "@gtmhub/shared/tree-renderer/utils";
import { ITeam, ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { IdMap } from "@gtmhub/util";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { INgRedux } from "@gtmhub/state-management";
import { OrganizationTreeRenderer } from "@gtmhub/teams/utils/organization-tree-renderer";

interface IOrganizationTreeScope extends IScope, IExtendedRootScopeService, IOrganizationRedux {
  collapseOrganizationTree(): void;
  expandOrganizationTree(): void;
  switchDragMode(): void;
  toggleAllTeamMembers(): void;
  toggleTeamMembers(teamId: string): void;
}

interface IOrganizationRedux {
  teamsIdMap: IdMap<ITeam>;
  teams: ITeam[];
  teamsFetched: boolean;
  teamsError: IUIError;
}

const bindStateToScope = (state: ITeamsStoreState): IOrganizationRedux => {
  return {
    teamsIdMap: state.teams.map,
    teams: state.teams.items,
    teamsFetched: state.teams.isFetched,
    teamsError: state.teams.error,
  };
};

export class OrganizationChartCtrl implements IStateInit {
  public static $inject = ["$scope", "$ngRedux", "TeamsActions", "UIErrorHandlingService"];

  constructor(
    private $scope: IOrganizationTreeScope,
    private $ngRedux: INgRedux,
    private teamsActions: TeamsActions,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    // actions at the top
    this.$scope.collapseOrganizationTree = this.collapseOrganizationTree;
    this.$scope.expandOrganizationTree = this.expandOrganizationTree;
    this.$scope.switchDragMode = this.switchDragMode;
    this.$scope.toggleAllTeamMembers = this.toggleAllTeamMembers;
    this.$scope.toggleTeamMembers = this.toggleTeamMembers;

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());
  }

  public stateInit(): IPromise<void> {
    return this.init();
  }

  private init = () => {
    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.teamsFetched,
        () => this.$scope.teamsError
      )
      .then(
        () => {
          this.$scope.teams = this.$scope.teams.sort((a, b) => (a.name === b.name ? 0 : a.name < b.name ? -1 : 1));
          // @todo consider if a loader is necessary and how to handle it
          // this.$scope.tree = createTeamsTree(this.$scope.teams, this.$scope.teamsIdMap);
          // this.$scope.loading.tree = false;
          // this.$scope.loading.treeUpdates = false;
          try {
            this.renderTree();
          } catch (e) {
            // AngularJS masks errors that occur in .then(), so we need to handle them ourselves.
            this.uiErrorHandlingService.handleModal(e);
          }
        },
        (error) => {
          console.error(error);
          // this.$scope.loading.tree = false;
          this.uiErrorHandlingService.handleModal(error);
        }
      );
  };

  private renderTree(): void {
    const tree = new OrganizationTreeRenderer(document.querySelector("#organization-tree-holder"));
    const nodeData = createTeamsTree(this.$scope.teams, this.$scope.teamsIdMap);

    // multiple rendering takes to test for bugs while developing
    // @todo: Disable debugging and render only once, when GVS-11824 is completed.
    tree.setOptions({ debug: true });
    tree
      .setOptions({ nodeMargin: { x: 10, y: 20 }, showRoot: false })
      .setNodeData(nodeData)
      .render();

    tree
      .setOptions({
        linkStartOffset: { x: 10, y: 3 },
        linkEndOffset: { x: -5, y: 40 },
        showRoot: true,
      })
      .render();

    tree
      // .setNodeData({ id: "dummy", depth: 0 } as TreeDataNode)
      .setOptions({
        nodeMargin: { x: 60, y: 10 },
        linkStartOffset: { x: 10, y: 30 },
        linkEndOffset: { x: 2, y: 31 },
        showRoot: false,
        zoom: 1,
        rootPosition: { x: 200, y: 50 },
        zoomMax: 1.5,
        zoomMin: 0.33,
      })
      .render();
  }

  private toggleTeamMembers(teamId: string): void {
    // @todo: GVS-11830
    alert(`Not Implemented: toggleTeamMembers for team: ${teamId}`);
  }

  private toggleAllTeamMembers(): void {
    // @todo: GVS-11829
    alert("Not Implemented: toggleAllTeamMembers");
  }

  private collapseOrganizationTree(): void {
    // @todo: GVS-11826
    alert("Not Implemented: collapseOrganizationTree");
  }

  private expandOrganizationTree(): void {
    // @todo: GVS-11826
    alert("Not Implemented: expandOrganizationTree");
  }

  private switchDragMode(): void {
    // @todo: part of GVS-11828
    alert("Not Implemented: switchDragMode");
  }
}
